import { Modal } from 'antd'
import type { FormInstance } from 'antd/es/form'
import { useNavigate } from 'react-router-dom'
import { buildClientPayloadFromForm, statusToLabel } from '../../clientFormShared'
import type { ClientFormValues } from '../../clientFormShared'
import type { UseUpdateClientMutationResult } from '../../hooks'
import type { ClientDetail, ClientStatusId, PbiGroupOption } from '../../types'
import { useToast } from '@/contexts/ToastContext'

type StatusConfirmModalProps = {
  open: boolean
  onClose: () => void
  form: FormInstance<ClientFormValues>
  editDetail: ClientDetail | null | undefined
  pbiGroups: PbiGroupOption[]
  logoBase64: string
  clientId: string
  currentEditStatusId: ClientStatusId
  updateMutation: UseUpdateClientMutationResult
}

export function StatusConfirmModal({
  open,
  onClose,
  form,
  editDetail,
  pbiGroups,
  logoBase64,
  clientId,
  currentEditStatusId,
  updateMutation,
}: StatusConfirmModalProps) {
  const navigate = useNavigate()
  const { addToast } = useToast()

  const handleOk = async () => {
    if (!editDetail) return
    try {
      const values = await form.validateFields()
      const nextStatusId: ClientStatusId = currentEditStatusId === 1 ? 2 : 1

      await updateMutation.mutateAsync({
        ...buildClientPayloadFromForm(values, pbiGroups, logoBase64, nextStatusId),
        clientId,
      })

      form.setFieldValue('statusId', nextStatusId)
      addToast(`${values.name} marked ${statusToLabel(nextStatusId).toLowerCase()}`, 'success')
      onClose()
      navigate('/clients')
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Unable to update status'
      addToast(message, 'error')
    }
  }

  return (
    <Modal
      open={open}
      title="Change client status"
      okText={currentEditStatusId === 2 ? 'Mark Active' : 'Mark Inactive'}
      okButtonProps={{
        danger: currentEditStatusId === 1,
        loading: updateMutation.isPending,
      }}
      onOk={() => void handleOk()}
      onCancel={onClose}
    >
      <p className="text-sm text-gray-700">
        Are you sure you want to set this client to{' '}
        <strong>{currentEditStatusId === 1 ? 'inactive' : 'active'}</strong>?
      </p>
    </Modal>
  )
}
